import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";

axios.defaults.withCredentials = true;

/**
 * Resolve the backend base URL at runtime.
 *
 * If the build-time REACT_APP_BACKEND_URL points to a different host than the
 * current page (e.g. the production frontend at https://flowtax.io was built
 * with a backend URL pointing to ai-dev-cost-calc.emergent.host), browser
 * requests become cross-origin. With cookie-based auth this triggers strict
 * CORS rules and the platform's Cloudflare layer responds with
 * `Access-Control-Allow-Origin: *` + no `Allow-Credentials: true`, which the
 * browser rejects as "Network Error".
 *
 * Falling back to same-origin (window.location.origin) avoids CORS entirely
 * for any deploy that fronts /api/* on the same domain as the app — which is
 * how Emergent deployments are wired.
 */
function resolveBackendUrl() {
  const buildUrl = process.env.REACT_APP_BACKEND_URL || "";
  if (typeof window === "undefined") return buildUrl;
  try {
    const buildHost = new URL(buildUrl).host;
    if (buildHost && buildHost !== window.location.host) {
      return window.location.origin;
    }
  } catch (_e) { /* malformed env var; fall through */ }
  return buildUrl;
}

const BACKEND_URL = resolveBackendUrl();
export const API_BASE = `${BACKEND_URL}/api`;

/**
 * Silent token refresh on 401.
 *
 * access_token cookie lives 12h; refresh_token lives 30d. Without this, once
 * the short access cookie expires (e.g. the app left open overnight) every
 * request returned 401 "Not authenticated" and the user was bounced to login,
 * even though a valid refresh cookie was present.
 *
 * On a 401 we call POST /auth/refresh exactly once (single-flight — the refresh
 * endpoint rotates + blocklists the old refresh token, so two concurrent
 * refreshes would trip "Refresh token reused" and log the user out), then retry
 * the original request. /auth/refresh and the login/logout family never trigger
 * a refresh (avoids loops); /auth/me is allowed to, so a returning user with
 * only the refresh cookie is restored transparently.
 */
let _refreshPromise = null;
let _onAuthFailure = null;
export function registerAuthFailureHandler(fn) { _onAuthFailure = fn; }

const _NO_REFRESH_PATHS = [
  "/auth/refresh", "/auth/login", "/auth/register",
  "/auth/google", "/auth/logout",
  "/portal/",   // client-portal is a separate auth domain (portal_token) — never refresh the firm session for it
];

axios.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { response, config } = error;
    if (!response || response.status !== 401 || !config || config._retry
        || _NO_REFRESH_PATHS.some((p) => (config.url || "").includes(p))) {
      return Promise.reject(error);
    }
    config._retry = true;
    try {
      if (!_refreshPromise) {
        _refreshPromise = axios.post(`${API_BASE}/auth/refresh`)
          .finally(() => { _refreshPromise = null; });
      }
      await _refreshPromise;
      return axios(config);           // retry the original request
    } catch (e) {
      if (_onAuthFailure) _onAuthFailure();
      return Promise.reject(error);
    }
  }
);

const AuthCtx = createContext(null);

// Flexible error formatter: FastAPI 422 returns array of {msg,...}
export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = authenticated
  const [state, setState] = useState(null);

  const refresh = useCallback(async () => {
    // CRITICAL: if returning from Google OAuth with #session_id=..., skip /me check.
    // AuthCallback will exchange the session and set cookies first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setState(null);
      return;
    }
    try {
      const { data } = await axios.get(`${API_BASE}/auth/me`);
      setState(data);
    } catch {
      setState(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // When a 401 can't be recovered by a token refresh, mark unauthenticated so
  // ProtectedRoute redirects to login instead of leaving a dead session.
  useEffect(() => {
    registerAuthFailureHandler(() => setState(false));
    return () => registerAuthFailureHandler(null);
  }, []);

  const login = useCallback(async (email, password, twoFA = {}) => {
    // twoFA may contain {totp_code} or {recovery_code} on second attempt
    await axios.post(`${API_BASE}/auth/login`, { email, password, ...twoFA });
    await refresh();
  }, [refresh]);

  const register = useCallback(async (payload) => {
    await axios.post(`${API_BASE}/auth/register`, payload);
    await refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await axios.post(`${API_BASE}/auth/logout`); }
    catch (err) { console.error("logout failed", err); }
    setState(false);
  }, []);

  const completeGoogle = useCallback(async (session_id) => {
    await axios.post(`${API_BASE}/auth/google`, { session_id });
    await refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ state, login, register, logout, completeGoogle, refresh }),
    [state, login, register, logout, completeGoogle, refresh]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}
